/**
 * The photo fan — the shelf's cards as a hand (D-032).
 *
 * The design authority, 2026-09-01, handed over a card-fan carousel and asked
 * for it *"for the photo component at the top of each survey screen."* This is
 * that carousel, ported into this codebase's own idiom — plain CSS on the
 * theme tokens, GSAP for the motion — and made to work under one thumb on a
 * 430px phone, which the hand-over (a hover-driven hero section) was not.
 *
 * ── What it is, and what it is not ──────────────────────────────────────────
 * PRESENTATIONAL and CONTROLLED. The shelf decides which cards exist
 * (`shelfModel`) and which one is fronted (`nextCenter`, src/ui/state/
 * photoFan.ts); this component lays them out in the seven slot poses, runs the
 * motion, and reports gestures back through `onFront`. It holds no survey
 * state and never touches the store.
 *
 * ── The front is a focus, and it moves ──────────────────────────────────────
 * The hand-over fixed the middle for seven cards or fewer and paged modulo
 * beyond that. On a phone the fronted card is the one whose face is readable
 * and whose whole area is a tap target, so it has to be movable: the arrows
 * (44px, brief §2), a horizontal swipe on the stage, the keyboard's arrows, or
 * a tap on a card that is peeking. It never wraps — see photoFan.ts.
 *
 * ── Tap a side card: it comes forward, and it may act too ───────────────────
 * A side card is rotated and overlapped, so a tap on it used to be swallowed
 * outright and turned into nothing but "come forward" — round 7's shelf-card
 * band was hidden while peeking, so acting on a name you could not read would
 * have been wrong. Round 8 task 5 stopped hiding the band; round 8.1 task 5
 * finishes the thought for a card whose tap already means something readable
 * — a captured photo's own viewer — by marking it `FanCard.actsWhenPeeking`:
 * the fan fronts it AND lets the SAME tap reach its own `onClick`, one motion
 * rather than two, instead of swallowing the click as it still does for every
 * other card. A card whose tap opens the camera (a shot still to take) keeps
 * the OLD front-only behaviour — its label being legible now is beside the
 * point; the picker itself is still the wrong door to open on a card the
 * surveyor only meant to bring forward. A keyboard or assistive activation
 * (`detail === 0`) is never intercepted either way — a screen-reader user has
 * already heard the card's name.
 *
 * ── Hover is a desktop nicety ───────────────────────────────────────────────
 * iOS fires `mouseenter` on the first touch, so the hand-over's hover
 * choreography would fight every tap on a phone. It runs only where the
 * pointer can actually hover: `(hover: hover) and (pointer: fine)`.
 *
 * ── Motion ──────────────────────────────────────────────────────────────────
 * The hand-over's three moves are kept: the elastic rise on entry, the slide
 * in from the side for a card coming into the window, the settle for a card
 * changing slot. A photo just taken rises into the front the way the hand
 * first appeared — it lands in your hand. `prefers-reduced-motion` sets every
 * pose without a tween. The spread is solved from the stage's real width and
 * the cards' real size, and re-solved on resize.
 */

import gsap from 'gsap';
import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import { FAN_HALF, fanSide, fanSpread, fanWindow, slotPosePx } from '../state/photoFan';

export interface FanCard {
  key: string;
  node: ReactNode;
  /**
   * What the card is showing — the photo's id, or '' for a shot still to
   * take. A shot's card keeps its KEY when its photo lands, so this is how
   * the fan sees the landing and lets the card rise into the hand.
   */
  stamp?: string;
  /**
   * Round 8.1 task 5: true for a card whose tap already opens something
   * legible (a captured photo's viewer) — tapping it while it only peeks
   * fronts it AND fires its own `onClick` in the same tap, rather than the
   * two taps every other card still needs. Unset (or false) for a card whose
   * tap opens the camera — still front-only, see the header comment.
   */
  actsWhenPeeking?: boolean;
}

export interface PhotoFanProps {
  cards: readonly FanCard[];
  /** The fronted card's key. Undefined only for an empty hand. */
  front: string | undefined;
  /** A gesture asked for this card to come forward. */
  onFront: (key: string) => void;
  /** What the arrows are for, to a screen reader — "Photos · Electrical". */
  label: string;
}

/** How far down the outer slots drop, as a fraction of the hand-over's 7.3rem. */
const DROP = 0.2;
/** Where a card rises from on entry, px. */
const ENTRY_Y = 120;
/** Where a card slides in from / out to, px. */
const SIDE_X = 480;
/** A horizontal move past this is a swipe, not a tap. */
const SWIPE_PX = 40;
/** How far a hovered card lifts, px — desktop only. */
const HOVER_LIFT = 40;

/** Card size when the DOM cannot say yet (matches --fan-card-w/h in theme.css). */
const FALLBACK_W = 168;
const FALLBACK_H = 208;
/** Past this many cards the dots would clip; a readout says where you are. */
const DOTS_MAX = 12;

export function PhotoFan({ cards, front, onFront, label }: PhotoFanProps): ReactNode {
  const stage = useRef<HTMLDivElement | null>(null);
  const entered = useRef(false);
  const visible = useRef<Set<string>>(new Set());
  const stamps = useRef<Map<string, string>>(new Map());
  const swipe = useRef<{ id: number; x: number; y: number } | null>(null);
  const swiped = useRef(false);

  const count = cards.length;
  const keys = cards.map((card) => card.key);
  const stampOf = (index: number): string => cards[index]?.stamp ?? '';
  const stampsKey = cards.map((card) => card.stamp ?? '').join('|');
  const centerAt = front === undefined ? -1 : keys.indexOf(front);
  const center = centerAt < 0 ? 0 : centerAt;
  const window_ = fanWindow(count, center);

  const step = (delta: number): void => {
    const key = keys[center + delta];
    if (key !== undefined) onFront(key);
  };

  /* ── layout and motion ─────────────────────────────────────────────────── */
  useEffect(() => {
    const node = stage.current;
    if (!node || count === 0) return;
    const elements = Array.from(node.querySelectorAll<HTMLElement>('.fan-card'));
    if (elements.length === 0) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const firstMount = !entered.current;
    const wasVisible = visible.current;

    const measure = (): { spread: number } => {
      const sample = elements[0]?.firstElementChild as HTMLElement | null;
      const w = sample?.offsetWidth || FALLBACK_W;
      const h = sample?.offsetHeight || FALLBACK_H;
      return { spread: fanSpread(node.clientWidth || FALLBACK_W * 2, w, h) };
    };
    let { spread } = measure();

    const pose = (slot: number): { x: number; y: number; rotation: number; scale: number; zIndex: number } => {
      const p = slotPosePx(slot, spread, DROP);
      return { x: p.x, y: p.y, rotation: p.rot, scale: p.scale, zIndex: p.z };
    };

    const place = (animated: boolean): void => {
      elements.forEach((el, index) => {
        const key = keys[index] ?? '';
        const slot = window_.get(index);
        const seen = wasVisible.has(key);
        // The same card, now showing something else: a shot's photo landed.
        const landed = stamps.current.has(key) && stamps.current.get(key) !== stampOf(index);
        if (slot !== undefined) {
          const target = { ...pose(slot), opacity: 1 };
          if (!animated || reduced) {
            gsap.set(el, target);
          } else if (firstMount || ((!seen || landed) && index === center)) {
            // The hand rises on entry; a photo just taken rises into the front.
            gsap.set(el, { x: 0, y: ENTRY_Y, rotation: 0, scale: 0.5, opacity: 0, zIndex: target.zIndex });
            gsap.to(el, {
              ...target,
              duration: 1.2,
              ease: 'elastic.out(1.05,.78)',
              delay: firstMount ? 0.2 + slot * 0.06 : 0,
              overwrite: 'auto',
            });
          } else if (!seen) {
            // Coming into the window from beyond the edge it was past.
            const fromRight = index > center;
            gsap.set(el, {
              x: fromRight ? SIDE_X : -SIDE_X,
              y: target.y,
              rotation: fromRight ? 30 : -30,
              scale: 0.5,
              opacity: 0,
              zIndex: target.zIndex,
            });
            gsap.to(el, { ...target, duration: 0.6, ease: 'power2.out', overwrite: 'auto' });
          } else {
            gsap.to(el, { ...target, duration: 0.5, ease: 'power2.out', overwrite: 'auto' });
          }
        } else if (seen && animated && !reduced) {
          const toLeft = index < center;
          gsap.to(el, {
            x: toLeft ? -SIDE_X : SIDE_X,
            opacity: 0,
            scale: 0.5,
            rotation: toLeft ? -30 : 30,
            zIndex: 0,
            duration: 0.4,
            ease: 'power2.in',
            overwrite: 'auto',
          });
        } else {
          gsap.set(el, { opacity: 0, scale: 0.3, x: index < center ? -SIDE_X : SIDE_X, y: 0, zIndex: 0 });
        }
      });
    };

    place(true);
    visible.current = new Set(keys.filter((_, index) => window_.has(index)));
    stamps.current = new Map(keys.map((key, index) => [key, stampOf(index)]));
    entered.current = true;

    /* Re-solve the spread when the stage changes width — a rotation, a split
     * view — and set the poses without a tween. ONLY on a real change of
     * width: an observer delivers its first notification on `observe()`, and
     * setting the final poses there, one frame after the delayed entry tween
     * was queued, is where the tween would record its start — the rise would
     * begin from where it ends and never be seen. */
    let lastWidth = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth === lastWidth) return;
      lastWidth = node.clientWidth;
      ({ spread } = measure());
      place(false);
    });
    observer.observe(node);

    /* ── hover, only where a pointer can hover ─────────────────────────── */
    const handlers: { el: HTMLElement; enter: () => void }[] = [];
    let leaveTimer: number | null = null;
    let onLeave: (() => void) | null = null;
    if (canHover && !reduced) {
      const entries = elements
        .map((el, index) => ({ el, slot: window_.get(index) }))
        .filter((entry): entry is { el: HTMLElement; slot: number } => entry.slot !== undefined)
        .sort((a, b) => a.slot - b.slot);
      const centerSlot = FAN_HALF;
      let active: number | null = null;

      const layoutHover = (hovered: number | null): void => {
        entries.forEach(({ el, slot }) => {
          const base = pose(slot);
          let { x, y, rotation, scale } = base;
          let delay = 0;
          if (hovered !== null) {
            const distance = Math.abs(slot - hovered);
            delay = distance * 0.02;
            if (slot === hovered) {
              y -= HOVER_LIFT;
              scale *= 1.08;
            } else {
              const normalized = (slot - centerSlot) / centerSlot;
              const push = 8 * (1 - Math.abs(normalized)) * (1 + 0.2 * Math.max(0, 3 - distance)) * spread * 16;
              if (slot < hovered) {
                x -= push;
                rotation -= 3 / (distance + 1);
              } else {
                x += push;
                rotation += 3 / (distance + 1);
              }
            }
          } else {
            delay = Math.abs(slot - centerSlot) * 0.02;
          }
          gsap.to(el, { x, y, rotation, scale, duration: 0.5, delay, ease: 'elastic.out(1,.75)', overwrite: 'auto' });
          gsap.set(el, { zIndex: base.zIndex });
        });
      };

      entries.forEach(({ el, slot }) => {
        const enter = (): void => {
          if (leaveTimer !== null) {
            window.clearTimeout(leaveTimer);
            leaveTimer = null;
          }
          if (active !== slot) {
            active = slot;
            layoutHover(slot);
          }
        };
        el.addEventListener('mouseenter', enter);
        handlers.push({ el, enter });
      });
      onLeave = (): void => {
        if (leaveTimer !== null) window.clearTimeout(leaveTimer);
        leaveTimer = window.setTimeout(() => {
          active = null;
          layoutHover(null);
        }, 50);
      };
      node.addEventListener('mouseleave', onLeave);
    }

    return () => {
      observer.disconnect();
      handlers.forEach(({ el, enter }) => el.removeEventListener('mouseenter', enter));
      if (onLeave) node.removeEventListener('mouseleave', onLeave);
      if (leaveTimer !== null) window.clearTimeout(leaveTimer);
    };
    // The key list, the stamps and the front are the whole input; `window_` derives from them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys.join('|'), stampsKey, center]);

  /* ── gestures ──────────────────────────────────────────────────────────── */

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (!event.isPrimary) return;
    swipe.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    swiped.current = false;
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    const start = swipe.current;
    swipe.current = null;
    if (!start || start.id !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
      swiped.current = true;
      step(dx < 0 ? 1 : -1);
      // The swipe's own trailing click, if the browser sends one, arrives in
      // this same task and is swallowed; a touch swipe often sends none, and
      // the flag must not lie in wait for the surveyor's next real tap.
      window.setTimeout(() => {
        swiped.current = false;
      }, 0);
    }
  };

  /**
   * The capture-phase tap: a swipe's trailing click is swallowed, and a tap on
   * a card that is not in front brings it forward. Round 8.1 task 5: a card
   * marked `actsWhenPeeking` also gets to act on that SAME tap — the event is
   * left alone (no `preventDefault`/`stopPropagation`) so it goes on to reach
   * the card's own `onClick` in the bubble phase, one tap doing both; every
   * other card is still swallowed here exactly as before, front-only.
   */
  const onClickCapture = (event: MouseEvent<HTMLDivElement>): void => {
    if (swiped.current) {
      swiped.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.detail === 0) return; // keyboard / assistive activation acts directly
    const cardEl = (event.target as HTMLElement).closest<HTMLElement>('.fan-card');
    if (!cardEl || cardEl.dataset['fanCenter'] === 'true') return;
    const key = cardEl.dataset['fanKey'];
    if (key === undefined) return;
    onFront(key);
    const tapped = cards.find((c) => c.key === key);
    if (!tapped?.actsWhenPeeking) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * A keyboard step carries focus to the new front card. Focus stays on the
   * old card otherwise, and once that card slides beyond the window its
   * wrapper turns `inert`, the browser drops focus to the body, and the
   * arrows go dead until the surveyor tabs back in.
   */
  const focusFront = (): void => {
    window.requestAnimationFrame(() => {
      stage.current
        ?.querySelector<HTMLElement>('[data-fan-center="true"] button, [data-fan-center="true"] a, [data-fan-center="true"] [tabindex]')
        ?.focus();
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
      focusFront();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
      focusFront();
    }
  };

  if (count === 0) return null;

  const chevron = (direction: 'left' | 'right'): ReactNode => (
    <svg
      className="fan__chev"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points={direction === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
    </svg>
  );

  return (
    <div className="fan" data-fan-count={count}>
      <div
        className="fan__stage"
        ref={stage}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          swipe.current = null;
        }}
        onClickCapture={onClickCapture}
        onKeyDown={onKeyDown}
      >
        {cards.map((card, index) => {
          const slot = window_.get(index);
          const isFront = index === center;
          return (
            <div
              key={card.key}
              className="fan-card"
              data-fan-key={card.key}
              data-fan-offset={index - center}
              data-fan-side={slot === undefined ? undefined : fanSide(slot)}
              data-fan-center={isFront ? 'true' : undefined}
              // Beyond the window: not in the tab order, not read aloud, not a
              // hit target — `inert` is all three, where `aria-hidden` alone
              // would leave a focusable button inside a hidden subtree.
              inert={slot === undefined}
            >
              {card.node}
            </div>
          );
        })}
      </div>

      {count > 1 ? (
        <div className="fan__nav">
          <button
            type="button"
            className="fan__arrow"
            aria-label={`Previous photo — ${label}`}
            disabled={center === 0}
            onClick={() => step(-1)}
          >
            {chevron('left')}
          </button>
          {count <= DOTS_MAX ? (
            <div className="fan__dots" aria-hidden="true">
              {cards.map((card, index) => (
                <span key={card.key} className={index === center ? 'fan__dot fan__dot--on' : 'fan__dot'} />
              ))}
            </div>
          ) : (
            <span className="fan__pos" data-legibility="meta" aria-hidden="true">
              {String(center + 1)} / {String(count)}
            </span>
          )}
          <button
            type="button"
            className="fan__arrow"
            aria-label={`Next photo — ${label}`}
            disabled={center === count - 1}
            onClick={() => step(1)}
          >
            {chevron('right')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
